import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomStepper from "../../v2/MUI_custom_component/Stepper";
import { getBuyerRolesAction } from "../../v2/Onboarding/auth/action";
import { fetchDigiLockerData } from "./API/Action";
import RoleStep from "./Components/RoleStep";
import UserDetailsStep from "./Components/UserDetailsStep";
import UserAddressStep from "./Components/OnboardBuyerAddress/UserAddressStep";
import FirmDetailsStep from "./Components/FirmDetailsStep";
import HumbeeModal from "../../v2/MUI_custom_component/Modal/HumbeeModal";
import DataLostSvg from "../Illustrration/components/DataLostSvg";
import { buildKycFromKycResponse } from "./Redux/kycDetailsBuilders";

export const OnboardBuyer = () => {
  const dispatch = useDispatch();
  const activeStep = useSelector(
    (state) => state.onboardBuyerReducer.activeStep,
  );
  const selectedRole = useSelector(
    (state) => state.onboardBuyerReducer.selectedRole,
  );
  const selectedSupplier = useSelector(
    (state) => state.entityReducer.selectedSupplier,
  );
  const isInfluencerRole = selectedRole?.region_role_id == null;
  
  const STEPS = [
    { id: "role", label: "Role" },
    { id: "user-details", label: "User Details" },
    {
      id: "user-address",
      label: isInfluencerRole ? "User Address" : "Firm Details",
    },
  ];

  const [digiLockerLoading, setDigiLockerLoading] = useState(false);
  // Address step raises this when its in-step preview is active (hides the stepper).
  const [addressPreview, setAddressPreview] = useState(false);

  const returnTransactionId = new URLSearchParams(window.location.search).get(
    "transaction_id",
  );

  useEffect(() => {
    // On leaving the buyer flow, clear the buyer state AND the shared FirmOnboarding
    // GST/PAN fields (GstPanInput writes there) so nothing leaks into a later
    // firm-onboarding session.
    return () => {
      dispatch({ type: "RESET_ONBOARD_BUYER" });
      dispatch({ type: "RESET_OPERATIONAL_DETAILS" });
      dispatch({ type: "RESET_PAN_DETAILS" });
      dispatch({ type: "SET_SHOW_GST_INPUT", payload: false });
      dispatch({ type: "SET_GST_NUMBER", payload: null });
      dispatch({ type: "SET_GST_DETAILS", payload: null });
    };
  }, [dispatch]);

  // A DigiLocker return is not a "refreshed mid-flow" case → don't prompt to resume.
  const [showResumeModal, setShowResumeModal] = useState(
    () => (activeStep > 0 || selectedRole != null) && !returnTransactionId,
  );

  useEffect(() => {
    if (!returnTransactionId) return;
    // The pocket holds the initiate session_id, needed for verification_data.
    const pocket = JSON.parse(
      sessionStorage.getItem("onboard_buyer_digilocker") || "{}",
    );
    sessionStorage.removeItem("onboard_buyer_digilocker");
    window.history.replaceState({}, "", window.location.pathname);
    setDigiLockerLoading(true);
    fetchDigiLockerData(returnTransactionId).then((res) => {
      setDigiLockerLoading(false);
      if (!res?.error)
        dispatch({
          type: "SET_KYC_DETAILS",
          // Normalize into the unified kycDetails shape. Keep the session id for the
          // final submit's verification_data.
          payload: buildKycFromKycResponse(
            { ...res.data, session_id: pocket.session_id || returnTransactionId },
            "DIGILOCKER",
          ),
        });
      else toast.error(res?.message || "Failed to fetch DigiLocker data");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBuyerRoles = useCallback(
    async (supplierId) => {
      dispatch({ type: "SET_BUYER_ROLES_LOADING", payload: true });
      const { data, error, message } = await getBuyerRolesAction(
        Number(supplierId),
        false,
        true,
      );
      dispatch({ type: "SET_BUYER_ROLES_LOADING", payload: false });

      if (error) {
        toast.error(message || "Failed to fetch buyer roles");
        return;
      }

      const roles = data?.roles || [];
      dispatch({
        type: "SET_BUYER_ROLES_DATA",
        payload: {
          manufacturer: {
            manufacturer_id: data?.manufacturer_id,
            manufacturer_name: data?.manufacturer_name,
            url: data?.url,
          },
          vcpRoles: roles.filter((r) => r.region_role_id !== null),
          influencerRoles: roles.filter((r) => r.region_role_id === null),
        },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedSupplier) fetchBuyerRoles(selectedSupplier);
  }, [selectedSupplier, fetchBuyerRoles]);

  const handleResume = () => setShowResumeModal(false);

  const handleStartOver = () => {
    // Clear the buyer state AND the shared FirmOnboarding GST/PAN fields, otherwise
    // GST details entered before a refresh linger into the fresh flow.
    dispatch({ type: "RESET_ONBOARD_BUYER" });
    dispatch({ type: "RESET_OPERATIONAL_DETAILS" });
    dispatch({ type: "RESET_PAN_DETAILS" });
    dispatch({ type: "SET_SHOW_GST_INPUT", payload: false });
    dispatch({ type: "SET_GST_NUMBER", payload: null });
    dispatch({ type: "SET_GST_DETAILS", payload: null });
    if (selectedSupplier) fetchBuyerRoles(selectedSupplier);
    setShowResumeModal(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 4, md: 8 },
        width: "100%",
        p: 2,
      }}>
      {/* The address preview is a distinct summary screen — hide the stepper there. */}
      {!addressPreview && (
        <CustomStepper
          steps={STEPS}
          activeStep={activeStep}
          orientation="horizontal"
          showFlex={false}
          type="horizontal-left"
          size="small"
          onStepClick={(stepIndex) =>
            dispatch({ type: "SET_ONBOARD_ACTIVE_STEP", payload: stepIndex })
          }
        />
      )}
      {activeStep === 0 && <RoleStep />}
      {activeStep === 1 &&
        (digiLockerLoading ? (
          <Box sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <UserDetailsStep />
        ))}
      {activeStep === 2 &&
        (isInfluencerRole ? (
          <UserAddressStep
            onPreviewChange={setAddressPreview}
            onRequestCancel={() => setShowResumeModal(true)}
          />
        ) : (
          <FirmDetailsStep
            onPreviewChange={setAddressPreview}
            onRequestCancel={() => setShowResumeModal(true)}
          />
        ))}
      <HumbeeModal
        open={showResumeModal}
        onClose={handleResume}
        onConfirm={handleStartOver}
        title="Are you sure you want to cancel?"
        subtitle="All data would be lost."
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DataLostSvg />}
      />
    </Box>
  );
};
